/**
 * cl-core/alt — alt·캡션·AI 고지 공식 (지시서 §1-4.4~6 · CL fix_1). 순수 TS.
 *
 * ⛔ 렌더 alt 는 DB `site_images.alt_ko`·`caption_ko` 를 쓰지 않는다(fix_1: 「조감도」 혼입 실측).
 *    항상 이 공식으로 만든다. 빈 값 0 — 재료가 비면 그 조각만 빠진다.
 * ⛔ 「조감도」는 `kind: 'official'`(시행·시공사 공식 이미지)에서만 쓴다(§0-8.7).
 */

export type ClImageKind = 'ai_render' | 'stage_card' | 'price_card' | 'region_card' | 'region_collage' | 'official' | 'satellite' | 'info_card' | 'ai_thumb';

export interface AltInput {
  name: string;
  sigungu?: string | null;
  dong?: string | null;
  totalUnits?: number | null;
  /** seo1 num W2 — 세대 고정 문구(lib/seo1/units-phrase · 「총 499세대(일반공급 459)」). 있으면 totalUnits 대신 쓴다. */
  unitsLabel?: string | null;
  stageLabel?: string | null;
  regionName?: string | null;
}

/** AI 고지 문구(히어로 1회 · 갤러리 머리 1회). */
export const AI_NOTICE = '이 이미지는 공개 자료를 바탕으로 만든 AI 상상도이며 실제와 다를 수 있습니다.';

/** ImageObject 용 고정값(§1-4.6). */
export const AI_CREDIT_TEXT = '카더라 AI 상상도';
export const AI_DIGITAL_SOURCE_TYPE = 'http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia';

function join(parts: Array<string | null | undefined>, sep = ' '): string {
  return parts.map((p) => (p == null ? '' : String(p).trim())).filter(Boolean).join(sep);
}

function nm(i: AltInput): string {
  return String(i?.name ?? '').trim();
}

function unitsText(n: number | null | undefined): string | null {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? `${n.toLocaleString('ko-KR')}세대` : null;
}

/** 상상도: 「{단지명} 단지 전경 AI 상상도 — {시군구} {동} {총세대}세대」 */
export function altAiRender(i: AltInput): string {
  const head = `${nm(i)} 단지 전경 AI 상상도`;
  const tail = join([i.sigungu, i.dong, (i.unitsLabel && i.unitsLabel.trim()) || unitsText(i.totalUnits)]);
  return tail ? `${head} — ${tail}` : head;
}

/** 단계 카드: 「{단지명} 사업 단계 안내 — 현재 {단계}」 */
export function altStageCard(i: AltInput): string {
  const head = `${nm(i)} 사업 단계 안내`;
  return i.stageLabel ? `${head} — 현재 ${i.stageLabel.trim()}` : head;
}

/** 시세 카드: 「{단지명} 주변 시세 안내 — {시군구} {동}」 */
export function altPriceCard(i: AltInput): string {
  const tail = join([i.sigungu, i.dong]);
  return tail ? `${nm(i)} 주변 시세 안내 — ${tail}` : `${nm(i)} 주변 시세 안내`;
}

/** 지역 파이프라인 카드·콜라주: 「{지역} 분양·정비사업 현황 안내」 */
export function altRegionCard(i: AltInput): string {
  return `${(String(i.regionName ?? '').trim() || nm(i))} 분양·정비사업 현황 안내`;
}

/** 공식 이미지(시행·시공사 제공): 「{단지명} 조감도」 — 「조감도」가 허용되는 유일한 자리. */
export function altOfficial(i: AltInput): string {
  // live2-r(재스윕 12 엄궁) — 공식 이미지는 조감도·사진·투시도가 섞인다(종류 필드 없음 · image-caption 규칙). 「조감도」 로 단정하지 않는다.
  return `${nm(i)} 공식 이미지`;
}

/** 위성사진: 「{단지명} 위성사진 — {시군구} {동}」 */
export function altSatellite(i: AltInput): string {
  const tail = join([i.sigungu, i.dong]);
  return tail ? `${nm(i)} 위성사진 — ${tail}` : `${nm(i)} 위성사진`;
}

/** 정보 카드(사전 생성 og 카드): 「{단지명} 단지 정보 카드」 */
export function altInfoCard(i: AltInput): string {
  return `${nm(i)} 단지 정보 카드`;
}

/** 목록 썸네일(AI 이미지 1:1 크롭): 「{단지명} 단지 썸네일 — AI 생성 이미지」. 히어로·갤러리 밖이라 고지 블록 대신 alt 로 밝힌다. */
export function altAiThumb(i: AltInput): string {
  return `${nm(i)} 단지 썸네일 — AI 생성 이미지`;
}

export function altFor(kind: ClImageKind, i: AltInput): string {
  switch (kind) {
    case 'ai_thumb': return altAiThumb(i);
    case 'satellite': return altSatellite(i);
    case 'info_card': return altInfoCard(i);
    case 'ai_render': return altAiRender(i);
    case 'stage_card': return altStageCard(i);
    case 'price_card': return altPriceCard(i);
    case 'region_card':
    case 'region_collage': return altRegionCard(i);
    case 'official': return altOfficial(i);
  }
}

const CAPTION_LABEL: Record<ClImageKind, string> = {
  ai_render: '단지 전경 AI 상상도',
  stage_card: '사업 단계 카드',
  price_card: '주변 시세 카드',
  region_card: '지역 현황 카드',
  region_collage: '지역 현황 카드',
  official: '공식 이미지', // live2-r — 「공식 조감도」 단정 금지(출처는 갤러리 아래 「이미지 출처」 줄)
  satellite: '위성사진',
  info_card: '단지 정보 카드',
  ai_thumb: 'AI 생성 썸네일',
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * 캡션: 「01 / 05 — 단지 전경 AI 상상도」.
 * `label` 을 주면 종류 라벨 대신 쓴다(예: 「단지 전경 AI 상상도(야간)」). 금지 단어 「조감도」는 official 외 차단.
 */
export function captionFor(index1: number, total: number, kind: ClImageKind, label?: string | null): string {
  let l = (label && label.trim()) || CAPTION_LABEL[kind];
  if (kind !== 'official') l = l.replace(/조감도/g, '상상도');
  return `${pad2(index1)} / ${pad2(total)} — ${l}`;
}

/** 「조감도」가 공식 이미지 밖에 들어갔는지(게이트·테스트용). */
export function violatesBirdseyeRule(text: string | null | undefined, kind: ClImageKind): boolean {
  return kind !== 'official' && /조감도/.test(String(text ?? ''));
}

/**
 * AI 생성 이미지 종류(image kind) 판별 — 단일 출처(hc3 H6). content-gates G7 ai_notice_count 가 이 종류가 있으면
 * 본문 AI 고지 «정확히 1회» 를 요구한다. 카페 생성기(cafe/gen/g2-common)·발행 관문 입력(content-v2/cafe-gate) 공용.
 */
export const AI_IMAGE_KIND_RE = /ai_render|ext_cafe|site_image/;
